import { CdkDragDrop, CdkDragHandle, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AiButtonComponent } from '@shared/components/formly';
import { ModalConfirmComponent } from '@shared/components/modal-confirm/modal-confirm.component';
import { UntilDestroy, untilDestroyed } from '@shared/utils';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { distinctUntilChanged, Subject } from 'rxjs';
import { AIBtnEvent, ModalService, QuizQuestionDetailV3 } from 'thkee-common';
@UntilDestroy()
@Component({
  selector: 'app-quiz',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgFor,
    NgIf,
    NgClass,
    DragDropModule,
    AngularSvgIconModule,
    CdkDragHandle,
    ModalConfirmComponent,
    AiButtonComponent,
  ],
  templateUrl: './quiz.component.html',
  styleUrl: './quiz.component.scss',
})
export class QuizFormComponent implements OnInit {
  @Input() questionType: 'multi_choice' | 'single_choice' | 'single_choice_in_multi' = 'multi_choice';
  @Input() relatedLectureOption: { label: string; value: string | number }[] = [];
  @Input() qaData$: Subject<QuizQuestionDetailV3> = new Subject<QuizQuestionDetailV3>();
  @Output() formValue = new EventEmitter<any>();
  @ViewChild('deleteQuestionModal') deleteQuestionModal!: ModalConfirmComponent;
  @Output() qaDeleteAnswer = new EventEmitter<string>();
  quizForm!: FormGroup;

  constructor(private fb: FormBuilder, private modalService: ModalService, private el: ElementRef) {}

  ngOnInit(): void {
    this.qaData$.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe({
      next: (res: QuizQuestionDetailV3) => {
        this.initForm(res);
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  // Initialize quiz form
  initForm(formValue: QuizQuestionDetailV3 = {} as QuizQuestionDetailV3) {
    this.quizForm = this.fb.group({
      lecture: new FormControl(null),
      question: new FormControl('', [Validators.required, Validators.minLength(6)]),
      answers: this.fb.array([], Validators.required),
    });

    if (formValue && formValue.answers?.length) {
      formValue.answers.forEach((answer: any) => {
        this.answers.push(this.createAnswer(true));
      });
    } else {
      this.answers.push(this.createAnswer(false));
      this.answers.push(this.createAnswer(false));
    }
    this.quizForm.patchValue(formValue);
  }

  // Reset quiz form
  resetForm() {
    // this.quizForm.reset();
    this.quizForm = this.fb.group({
      lecture: new FormControl(null),
      question: new FormControl('', [Validators.required, Validators.minLength(6)]),
      answers: this.fb.array([this.createAnswer(false), this.createAnswer(false)], Validators.required),
    });

    if (this.questionType === 'single_choice_in_multi' || this.questionType === 'single_choice') {
      this.answers.at(0).patchValue({ is_correct: true });
    }
  }

  // Get quiz answer
  get answers(): FormArray<FormGroup> {
    return this.quizForm.get('answers') as FormArray<FormGroup>;
  }

  // Populate quiz Answer
  createAnswer(isSave: boolean = true): FormGroup {
    if (this.questionType === 'multi_choice') {
      return this.fb.group({
        answer: new FormControl('', Validators.required),
        expected: new FormControl(''),
        id: new FormControl(null),
        quiz_question: new FormControl(null),
        is_correct: new FormControl(false),
        isSaved: new FormControl(isSave),
      });
    } else {
      return this.fb.group({
        answer: new FormControl(''),
        expected: new FormControl(''),
        id: new FormControl(null),
        quiz_question: new FormControl(null),
        is_correct: new FormControl(false),
        isSaved: new FormControl(isSave),
      });
    }
  }

  // Add new answer
  addAnswer() {
    this.answers.push(this.createAnswer(false));
  }

  // Remove answer
  removeAnswer(index: number, answerId: string) {
    this.modalService.confirm({
      title: $localize`Delete Item`,
      message: $localize`Are you sure you want to remove this item?`,
      onConfirm: () => {
        this.deleteQA(index, answerId);
      },
    });
  }

  // Save quiz answer
  isEdit: boolean[] = [];
  saveAnswer(index: number) {
    this.answers.at(index).patchValue({ isSaved: true });
    this.isEdit[index] = true;
  }

  // Edit quiz answer
  editAnswer(index: number) {
    this.answers.at(index).patchValue({ isSaved: false });
    this.isEdit[index] = false;
  }

  // Check specific quiz answer validation
  isAnswerValid(index: number): boolean {
    return this.answers.at(index).valid;
  }

  // Check full quiz form validation
  isFormValid(): boolean {
    return this.quizForm.valid;
  }

  // Check quiz correct answer
  selectCorrectAnswer(index: number) {
    if (this.questionType === 'single_choice_in_multi' || this.questionType === 'single_choice') {
      this.answers.controls.forEach((answer, i) => {
        if (index === i) {
          if (this.questionType === 'single_choice') {
            answer.patchValue({ is_correct: i === index, answer: 'True' });
          } else {
            answer.patchValue({ is_correct: i === index });
          }
        } else {
          if (this.questionType === 'single_choice') {
            answer.patchValue({ is_correct: false, answer: 'False' });
          } else {
            answer.patchValue({ is_correct: false });
          }
        }
      });
    }
  }

  // Drag & Drop Quiz Answer
  drop(event: CdkDragDrop<FormGroup[]>) {
    moveItemInArray(this.answers.controls, event.previousIndex, event.currentIndex);
  }

  // Generate Alphabet array for quiz
  getAlphabetArray(i: number): string {
    return String.fromCharCode(65 + i);
  }

  // Access form
  getForm(): FormGroup {
    return this.quizForm;
  }

  // Access form value and removed isSave key
  getFormValue(): any {
    if (this.questionType === 'single_choice') {
      this.answers.controls.forEach((answer, i) => {
        if (i === 0) {
          answer.patchValue({ answer: 'True' });
        } else {
          answer.patchValue({ answer: 'False' });
        }
      });
    }
    const formValue = this.quizForm.getRawValue();
    const cleanedAnswers = formValue.answers.map(({ isSaved, ...rest }: any) => rest);

    return {
      ...formValue,
      answers: cleanedAnswers,
    };
  }

  // Delete question from Quiz
  deleteQA(index: number, answerId: string): void {
    this.answers.removeAt(index);
    this.qaDeleteAnswer.emit(answerId);
  }

  // Mark As Touch
  touch() {
    this.quizForm.get('question')?.markAsTouched();
    this.answers.controls.forEach((answerGroup: FormGroup) => {
      answerGroup.markAsTouched({ onlySelf: false });
      Object.keys(answerGroup.controls).forEach((key) => {
        answerGroup.get(key)?.markAsTouched({ onlySelf: false });
      });
    });

    // Scroll to validation position
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector('.ng-invalid');
    if (firstInvalidControl) {
      firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalidControl.focus?.();
    }
  }

  // Create full quiz with AI
  createQuizWithAI(event: AIBtnEvent) {
    console.log('🚀 ~ QuizFormComponent ~ createQuizWithAI ~ event:', event);
  }

  // Create quiz question with AI
  createQuizQuestionWithAI(event: AIBtnEvent) {
    console.log('🚀 ~ QuizFormComponent ~ createQuizQuestionWithAI ~ event:', event);
  }

  // Create quiz answer with AI
  createQuizAnswerWithAI(event: AIBtnEvent, index: number, is_correct: boolean) {
    console.log('🚀 ~ QuizFormComponent ~ createQuizQuestionWithAI ~ event:', event);
    console.log('🚀 ~ QuizFormComponent ~ createQuizQuestionWithAI ~ index:', index);
    console.log('🚀 ~ QuizFormComponent ~ createQuizQuestionWithAI ~ is_correct:', is_correct);
  }
}
